import { MessageSenderType } from "@redotech/cockroachdb-codegen/tables/shopper-ai/message-sender-type";
import { ShopperAgentGenerationMode } from "@redotech/cockroachdb-codegen/tables/shopper-ai/shopper-agent-generation-mode";
import { ShopperAgentOutboundMessageKind } from "@redotech/cockroachdb-codegen/tables/shopper-ai/shopper-agent-outbound-message-kind";
import { ShopperAiIntegrationType } from "@redotech/cockroachdb-codegen/tables/shopper-ai/shopper-ai-integration-type";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { PhoneNumberProvider } from "@redotech/redo-common-model/phone/phone-number";
import { bandwidthWebhookSchema } from "@redotech/redo-communication-primitives/text-messaging/bandwidth-zod";
import { smsMessageMetadataSchema_ } from "@redotech/redo-communication-primitives/text-messaging/message-metadata";
import { RedoSmsErrorCategory } from "@redotech/redo-communication-primitives/text-messaging/sms-error-codes";
import { telnyxMessageWebhookSchema } from "@redotech/redo-communication-primitives/text-messaging/telnyx-zod";
import { parseElementId } from "@redotech/redo-marketing-email-builder-common/clickable-element-primitives";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  baseCustomerEventSchema,
  CustomerEventSource,
  CustomerEventType,
  outreachStampSchema,
} from "./base";
import {
  SesBounceEventSchema,
  SesClickEventSchema,
  SesComplaintEventSchema,
  SesDeliveryEventSchema,
  SesOpenEventSchema,
  SesSendEventSchema,
} from "./ses-events";

export enum BounceType {
  PERMANENT = "PERMANENT",
  TRANSIENT = "TRANSIENT",
  UNDETERMINED = "UNDETERMINED",
}

export enum ReputationImpact {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  SEVERE = "severe",
}

export enum OutreachType {
  SMS = "sms",
  MMS = "mms",
  EMAIL = "email",
}

export enum SmsDirection {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

/** How an outbound shopper agent message passed the human review queue. */
export enum HumanQueueReview {
  /** The send skipped the queue. */
  NONE = "none",
  /** A reviewer sent it as is. */
  APPROVED = "approved",
  /** A reviewer changed it before sending. */
  EDITED = "edited",
}

export function humanQueueReviewOf(
  contentModified: boolean | null,
): HumanQueueReview {
  if (contentModified === null) {
    return HumanQueueReview.NONE;
  }
  return contentModified ? HumanQueueReview.EDITED : HumanQueueReview.APPROVED;
}

const smsWebhookCustomerEventTypes = [
  CustomerEventType.INBOUND_RECEIVED,
  CustomerEventType.OUTREACH_SEND,
  CustomerEventType.OUTREACH_DELIVERY,
  CustomerEventType.OUTREACH_FAILED,
  CustomerEventType.OUTREACH_OPEN,
] as const;
export type SmsWebhookCustomerEventType =
  (typeof smsWebhookCustomerEventTypes)[number];

// Outbound webhooks have full metadata with campaign/template context
const normalizedOutboundSmsWebhookSchema = z.object({
  provider: z.enum(PhoneNumberProvider),
  eventType: z.enum(smsWebhookCustomerEventTypes),
  messageId: z.string(),
  timestamp: z.coerce.date(),
  to: z.array(z.string()),
  from: z.string(),
  text: z.string().optional(),
  media: z
    .array(
      z.object({
        url: z.string(),
        contentType: z.string().optional(), // provided by Telnyx, but not Bandwidth
      }),
    )
    .optional(),
  segmentCount: z.number(),
  messageType: z.literal(OutreachType.SMS).or(z.literal(OutreachType.MMS)),
  direction: z.literal(SmsDirection.OUTBOUND),
  metadata: smsMessageMetadataSchema_,
  // Carrier fee in micros (only present for Telnyx, undefined for Bandwidth)
  carrierFeeMicros: z.number().optional(),
  // Error information (only present for MESSAGE_FAILED events)
  errors: z
    .array(
      z.object({
        code: z.string(),
        message: z.string(),
        shouldCreateSendEvent: z.boolean(),
        redoErrorCategory: createEnum(RedoSmsErrorCategory, {
          enumName: "RedoSmsErrorCategory",
        }).nullish(),
      }),
    )
    .optional(),
  // Provider's detection of SMS action (STOP, START, HELP, etc.) - for inbound messages
  detectedSmsAction: z.string().nullish(),
  // The original provider-specific webhook (Bandwidth or Telnyx)
  // This is stored both here and at the root CustomerEvent.rawEvent for compatibility
  rawEvent: z
    .union([telnyxMessageWebhookSchema, bandwidthWebhookSchema])
    .optional(),
});
export type NormalizedOutboundSmsWebhook = z.infer<
  typeof normalizedOutboundSmsWebhookSchema
>;

// Inbound webhooks have minimal metadata (just teamId and phoneNumber)
const normalizedInboundSmsWebhookSchema = normalizedOutboundSmsWebhookSchema
  .omit({ metadata: true })
  .extend({
    metadata: smsMessageMetadataSchema_.pick({ category: true }),
    direction: z.literal(SmsDirection.INBOUND),
  });

const normalizedSmsWebhookSchema_ = z.discriminatedUnion("direction", [
  normalizedOutboundSmsWebhookSchema,
  normalizedInboundSmsWebhookSchema,
]);
export const normalizedSmsWebhookSchema: z.ZodType<NormalizedSmsWebhook> =
  normalizedSmsWebhookSchema_;
export type NormalizedSmsWebhook = z.infer<typeof normalizedSmsWebhookSchema_>;

const normalizedSmsWebhookSchemaWithoutRawEvent_ = z.discriminatedUnion(
  "direction",
  [
    normalizedOutboundSmsWebhookSchema.omit({ rawEvent: true }),
    normalizedInboundSmsWebhookSchema.omit({ rawEvent: true }),
  ],
);
export const normalizedSmsWebhookSchemaWithoutRawEvent: z.ZodType<NormalizedSmsWebhookWithoutRawEvent> =
  normalizedSmsWebhookSchemaWithoutRawEvent_;

// Used for backwards compatibility at the DB layer, which stores the raw event at the root level
export type NormalizedSmsWebhookWithoutRawEvent = z.infer<
  typeof normalizedSmsWebhookSchemaWithoutRawEvent_
>;

/** Topic and strategy ids are raw CRDB INT8 decimal strings like `supportMessageId`, never the prefixed wire form. */
const shopperAgentMessageFactsSchema = z.object({
  /** Null when the thread had no active outreach. */
  outreachTopicId: z.string().nullish(),
  /** The shopper_agent_conversation for the thread. Every writer has one, so downstream it marks a Commerce Agent event. */
  conversationTopicId: z.string().optional(),
  /** The strategy of the outreach this event falls under. */
  strategyId: z.string().optional(),
});

export const shopperAgentOutboundFactsSchema =
  shopperAgentMessageFactsSchema.extend({
    outboundMessageKind: createEnum(ShopperAgentOutboundMessageKind, {
      enumName: "ShopperAgentOutboundMessageKind",
    }).optional(),
    generationMode: createEnum(ShopperAgentGenerationMode, {
      enumName: "ShopperAgentGenerationMode",
    }).optional(),
    humanQueueReview: createEnum(HumanQueueReview, {
      enumName: "HumanQueueReview",
    }).optional(),
  });

const shopperAgentMessageFieldsSchema = z.object({
  /** Backfills must write dotted paths; setting the whole object drops its other fields. */
  shopperAgent: shopperAgentMessageFactsSchema.optional(),
  shopperSupportTopicId: z.string().optional(),
  /** Prefixed `cthr_` form, matching the SHOPPER_AGENT_* action events it joins to. */
  channelThreadId: z.string().optional(),
  /** The message's own integration, so an iMessage that fell back to SMS reads as SMS. */
  supportMessageIntegrationType: createEnum(ShopperAiIntegrationType, {
    enumName: "ShopperAiIntegrationType",
  }).optional(),
  /** The trigger of the outreach this event falls under, not a per-message value. */
  triggerKey: z.string().optional(),
});

const shopperAgentOutboundFieldsSchema = z.object({
  shopperAgent: shopperAgentOutboundFactsSchema.optional(),
  /** The saved message's sender type. Outreach saves as `auto_reply`, so only trust it to spot merchant replies. */
  supportMessageSenderType: createEnum(MessageSenderType, {
    enumName: "MessageSenderType",
  }).optional(),
});

const _OutreachAnalyticsEventBaseSchema = baseCustomerEventSchema.extend({
  template: z.string().optional(),
  automation: z.string().optional(),
  automationStepId: z.string().optional(),
  campaign: z.string().optional(),
  marketingMessageId: z.string().optional(),
  marketingConversationId: z.string().optional(),
  supportMessageId: z.string().optional(),
  category: z.string().optional(),
  /**
   * The email address or phone number that the outreach was sent from.
   * Currently only populated for SES-based email events, not AMP emails or SMS,
   * therefore it should not be considered reliable.
   */
  fromChannelId: z.string().optional(),
  recipientId: z.string().optional(),
  channelId: z.string(),
  orderId: zExt.objectId().optional(),
  returnId: zExt.objectId().optional(),
  externalMessageId: z.string().optional(),
  templateName: z.string().optional(),
  ...outreachStampSchema.shape,
  ...shopperAgentMessageFieldsSchema.shape,
});

const _EmailOutreachEventBaseSchema = _OutreachAnalyticsEventBaseSchema.extend({
  channel: z.literal(MarketingChannel.EMAIL),
  recipientDomain: z.string().optional(),
  outgoingIp: z.string().optional(),
  configurationSet: z.string().optional(),
  ipPool: z.string().optional(),
  subject: z.string().optional(),
});
export type EmailOutreachBaseEvent = z.infer<
  typeof _EmailOutreachEventBaseSchema
>;

const rawSmsWebhookSchema_ = z.union([
  telnyxMessageWebhookSchema,
  bandwidthWebhookSchema,
]);
export type RawSmsWebhook = z.infer<typeof rawSmsWebhookSchema_>;
const rawSmsWebhookSchema: z.ZodType<RawSmsWebhook> = rawSmsWebhookSchema_;

const _SmsOutreachEventBaseSchema = _OutreachAnalyticsEventBaseSchema.extend({
  channel: z.literal(MarketingChannel.SMS),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: rawSmsWebhookSchema.optional(),
  normalizedWebhook: normalizedSmsWebhookSchemaWithoutRawEvent.optional(),
});
export type SmsOutreachBaseEvent = z.infer<typeof _SmsOutreachEventBaseSchema>;

export const outreachClickRequestSignalsSchema = z.object({
  secFetchUser: z.string().optional(),
  secFetchMode: z.string().optional(),
  secChUa: z.string().optional(),
  secPurpose: z.string().optional(),
  xPurpose: z.string().optional(),
  refererOrigin: z.string().optional(),
});
export type OutreachClickRequestSignals = z.infer<
  typeof outreachClickRequestSignalsSchema
>;

const outreachClickEvidenceSchema = z.object({
  userAgent: z.string().optional(),
  ipAddress: z.string().optional(),
  clickedUrl: z.string().optional(),
  requestSignals: outreachClickRequestSignalsSchema.optional(),
});

const messageCostSchema_ = z.object({
  totalMicros: z.number(),
  rate: z.object({ amountMicros: z.number(), currency: z.string() }).nullish(),
  currency: z.string().nullish(),
  count: z.number(),
  additionalFees: z
    .object({
      carrierFee: z.object({ amountMicros: z.number(), currency: z.string() }),
    })
    .nullish(),
  pricingContext: z.object({
    messageType: createEnum(OutreachType, { enumName: "OutreachType" }),
    isoCountryCode: z.string().nullish(),
  }),
});
export const messageCostSchema: z.ZodType<MessageCost> = messageCostSchema_;
export interface MessageCost extends z.infer<typeof messageCostSchema_> {}

const EmailOutreachSendEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_SEND),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: SesSendEventSchema.optional(),
  cost: messageCostSchema.optional(),
  ...shopperAgentOutboundFieldsSchema.shape,
});
export interface EmailOutreachSendEvent extends z.infer<
  typeof EmailOutreachSendEventSchema
> {}

const SmsOutreachSendEventSchema = _SmsOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_SEND),
  cost: messageCostSchema.optional(),
  ...shopperAgentOutboundFieldsSchema.shape,
});
export interface SmsOutreachSendEvent extends z.infer<
  typeof SmsOutreachSendEventSchema
> {}

export type ShopperAgentInboundEventFields = z.infer<
  typeof shopperAgentMessageFieldsSchema
>;

/** The thread-level subset, known before any message is saved. */
export type ShopperAgentTopicEventFields = Pick<
  ShopperAgentInboundEventFields,
  "shopperAgent" | "shopperSupportTopicId" | "channelThreadId" | "triggerKey"
>;

export type ShopperAgentSendEventFields = Pick<
  SmsOutreachSendEvent,
  | keyof typeof shopperAgentMessageFieldsSchema.shape
  | keyof typeof shopperAgentOutboundFieldsSchema.shape
>;

export const OutreachSendEventSchema_ = z.discriminatedUnion("channel", [
  EmailOutreachSendEventSchema,
  SmsOutreachSendEventSchema,
]);
export const OutreachSendEventSchema: z.ZodType<OutreachSendEvent> =
  OutreachSendEventSchema_;
export type OutreachSendEvent = z.infer<typeof OutreachSendEventSchema_>;

const EmailOutreachDeliveryEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_DELIVERY),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: SesDeliveryEventSchema.optional(),
  deliveryTimeMs: z.number().optional(),
  smtpResponse: z.string().optional(),
  remoteMtaIp: z.string().optional(),
});
export interface EmailOutreachDeliveryEvent extends z.infer<
  typeof EmailOutreachDeliveryEventSchema
> {}

const SmsOutreachDeliveryEventSchema = _SmsOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_DELIVERY),
});
export interface SmsOutreachDeliveryEvent extends z.infer<
  typeof SmsOutreachDeliveryEventSchema
> {}

export const OutreachDeliveryEventSchema_ = z.discriminatedUnion("channel", [
  EmailOutreachDeliveryEventSchema,
  SmsOutreachDeliveryEventSchema,
]);
export const OutreachDeliveryEventSchema: z.ZodType<OutreachDeliveryEvent> =
  OutreachDeliveryEventSchema_;
export type OutreachDeliveryEvent = z.infer<
  typeof OutreachDeliveryEventSchema_
>;

// SMS doesn't have open events, only email does
const EmailOutreachOpenEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_OPEN),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: SesOpenEventSchema.optional(),
  // Email open specific fields
  userAgent: z.string().optional(),
  ipAddress: z.string().optional(),
  source: createEnum(
    [CustomerEventSource.SES, CustomerEventSource.LINK_SHORTENER],
    {
      enumName: "CustomerEventSource",
      isPartial: true,
      originalEnum: CustomerEventSource,
    },
  ).optional(),
});
export interface EmailOutreachOpenEvent extends z.infer<
  typeof EmailOutreachOpenEventSchema
> {}

export const OutreachOpenEventSchema_ = EmailOutreachOpenEventSchema;
export const OutreachOpenEventSchema: z.ZodType<OutreachOpenEvent> =
  OutreachOpenEventSchema_;
export type OutreachOpenEvent = EmailOutreachOpenEvent;

const EmailOutreachClickEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_CLICK),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: SesClickEventSchema.optional(),
  ...outreachClickEvidenceSchema.shape,
  shopperAgent: shopperAgentOutboundFactsSchema.optional(),
  expectedDistinctLinkCount: z.number().optional(),
  /** An identifier for the clickable element that was clicked. Might have a semantic name, might not */
  elementId: z
    .string()
    .transform((val) => parseElementId(val))
    .optional(),
  /** Which email block the click occurred within */
  blockId: z.string().optional(),
  source: createEnum(
    [CustomerEventSource.SES, CustomerEventSource.LINK_SHORTENER],
    {
      enumName: "CustomerEventSource",
      isPartial: true,
      originalEnum: CustomerEventSource,
    },
  ).optional(),
});
export interface EmailOutreachClickEvent extends z.infer<
  typeof EmailOutreachClickEventSchema
> {}

const SmsOutreachClickEventSchema = _SmsOutreachEventBaseSchema
  .extend({
    eventType: z.literal(CustomerEventType.OUTREACH_CLICK),
    ...outreachClickEvidenceSchema.shape,
    shopperAgent: shopperAgentOutboundFactsSchema.optional(),
    source: z.literal(CustomerEventSource.LINK_SHORTENER).optional(),
  })
  .omit({ rawEvent: true, normalizedWebhook: true }); // No rawEvent for SMS clicks

export const OutreachClickEventSchema_ = z.discriminatedUnion("channel", [
  EmailOutreachClickEventSchema,
  SmsOutreachClickEventSchema,
]);
export const OutreachClickEventSchema: z.ZodType<OutreachClickEvent> =
  OutreachClickEventSchema_;

export type OutreachClickEvent = z.infer<typeof OutreachClickEventSchema_>;

/** What a shortened link carries so its click lands with the same facts as the send it was in. */
export type ShopperAgentClickEventFields = Pick<
  z.infer<typeof SmsOutreachClickEventSchema>,
  "shopperAgent" | "shopperSupportTopicId" | "channelThreadId" | "triggerKey"
>;

// SMS doesn't have bounce events, only email does
const EmailOutreachBounceEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_BOUNCE),
  bounceType: createEnum(BounceType, { enumName: "BounceType" }).optional(),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: SesBounceEventSchema.optional(),
  bounceSubtype: z.string().optional(),
  bounceDiagnosticCode: z.string().optional(),
  bounceStatus: z.string().optional(),
  reputationImpact: createEnum(ReputationImpact, {
    enumName: "ReputationImpact",
  }).optional(),
});
export interface EmailOutreachBounceEvent extends z.infer<
  typeof EmailOutreachBounceEventSchema
> {}

export const OutreachBounceEventSchema_ = EmailOutreachBounceEventSchema;
export const OutreachBounceEventSchema: z.ZodType<OutreachBounceEvent> =
  OutreachBounceEventSchema_;
export type OutreachBounceEvent = EmailOutreachBounceEvent;

// SMS doesn't have complaint events, only email does
const EmailOutreachComplaintEventSchema = _EmailOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_COMPLAINT),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: SesComplaintEventSchema.optional(),
  complaintFeedbackType: z.string().optional(),
  reputationImpact: createEnum(ReputationImpact, {
    enumName: "ReputationImpact",
  }).optional(),
});
export interface EmailOutreachComplaintEvent extends z.infer<
  typeof EmailOutreachComplaintEventSchema
> {}

export const OutreachComplaintEventSchema_ = EmailOutreachComplaintEventSchema;
export const OutreachComplaintEventSchema: z.ZodType<OutreachComplaintEvent> =
  OutreachComplaintEventSchema_;
export type OutreachComplaintEvent = EmailOutreachComplaintEvent;

// Failed events are only for SMS, email failures come through as bounces
const SmsOutreachFailedEventSchema = _SmsOutreachEventBaseSchema.extend({
  eventType: z.literal(CustomerEventType.OUTREACH_FAILED),
  errorCode: z.string(),
  redoErrorCategory: createEnum(RedoSmsErrorCategory, {
    enumName: "RedoSmsErrorCategory",
  }).nullish(),
  errorMessage: z.string().optional(),
});
export interface SmsOutreachFailedEvent extends z.infer<
  typeof SmsOutreachFailedEventSchema
> {}

export const OutreachFailedEventSchema_ = SmsOutreachFailedEventSchema;
export const OutreachFailedEventSchema: z.ZodType<OutreachFailedEvent> =
  OutreachFailedEventSchema_;
export type OutreachFailedEvent = SmsOutreachFailedEvent;

export const InboundReceivedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.INBOUND_RECEIVED),
  category: z.string().optional(),
  channel: createEnum(MarketingChannel, { enumName: "MarketingChannel" }),
  channelId: z.string(),
  marketingConversationId: z.string().optional(),
  /** The support-v2 message row this text was stored as. */
  supportMessageId: z.string().optional(),
  ...shopperAgentMessageFieldsSchema.shape,
  cost: messageCostSchema.optional(),
  /** @deprecated Read promoted top-level fields instead. This field will be removed after backfilling top level fields */
  rawEvent: rawSmsWebhookSchema.optional(),
  normalizedWebhook: normalizedSmsWebhookSchemaWithoutRawEvent.optional(),
});
export const InboundReceivedEventSchema: z.ZodType<InboundReceivedEvent> =
  InboundReceivedEventSchema_;
export interface InboundReceivedEvent extends z.infer<
  typeof InboundReceivedEventSchema_
> {}
/** What the webhook builds and the support pipeline enqueues once the row exists. */
export const inboundReceivedEventInputSchema = InboundReceivedEventSchema_.omit(
  { _id: true },
);
export type InboundReceivedEventInput = z.infer<
  typeof inboundReceivedEventInputSchema
>;

export type CampaignRecipientMetricEvent =
  | OutreachOpenEvent
  | OutreachClickEvent;
